import {
    Button,
    createStyles,
    Modal,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import { connect } from "react-redux";
import {
    useGetMyProfileQuery,
    useUpdateUserProfileMutation
} from "../../api/accountApiSlice";
import { Administrator } from "../../models/security/Roles";
import Constants from "../../models/utility/Constants";
import { IAppState } from "../../store";
import { useAppSelector } from "../../store/hooks";
import LoaderButton from "../shared/LoaderButton";

type TermsAcceptanceDialogProps = WithStyles<typeof styles>;

const styles = (theme: Theme) =>
    createStyles({
        body: {
            [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: theme.spacing(4),
            textAlign: "center"
        },
        buttonWrapper: {
            display: "flex",
            flexDirection: "row",
            rowGap: theme.spacing(1.5),
            columnGap: theme.spacing(1.5),
            [theme.breakpoints.down("sm")]: {
                flexDirection: "column"
            }
        },
        bodyText: {
            [theme.breakpoints.down("xs")]: { fontSize: 16 },
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            marginBottom: theme.spacing(3),
            textAlign: "center"
        },
        header: {
            borderBottom: `8px solid ${theme.palette.primary.main}`,
            padding: theme.spacing(2),
            textAlign: "center",
            width: "100%"
        },
        headerText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.primary.main,
            fontWeight: "bold",
            margin: "0 auto"
        },
        paper: {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            margin: theme.spacing(2),
            maxWidth: 600,
            outline: "none",
            width: "100%"
        }
    });

function TermsAcceptanceDialog({ classes }: TermsAcceptanceDialogProps) {
    const baseUrl = useAppSelector(
        (state) => state.appConfigStoreState.salesPageUrl
    );
    const user = useAppSelector((state) => state.userStoreState.user);

    const { enqueueSnackbar } = useSnackbar();

    // If there is an authenticated user, check if they have accepted the terms
    // Else, do not show the dialog
    const getMyProfileQuery = useGetMyProfileQuery(undefined, {
        skip: !user?.email
    });
    const [updateUserProfileMutation, updateUserProfileMutationData] =
        useUpdateUserProfileMutation();

    const showOpen =
        !!user?.email &&
        !user?.roles.includes(Administrator) &&
        !getMyProfileQuery.isLoading &&
        !getMyProfileQuery.data?.isTermsAccepted;

    const onAccept = async () => {
        try {
            await updateUserProfileMutation({
                isTermsAccepted: true
            }).unwrap();

            getMyProfileQuery.refetch();

            enqueueSnackbar("Successfully accepted Terms and Conditions", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "success"
            });
        } catch (e) {
            console.error(e);

            enqueueSnackbar("Failed to update Terms and Conditions", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "error"
            });
        }
    };

    const onViewTerms = () => {
        window.open(Constants.Routes.TermsAndConditions({ baseUrl }), "_blank");
    };

    return (
        <Modal open={showOpen} className={classes.body}>
            <Paper className={classes.paper} elevation={2}>
                <div className={classes.header}>
                    <Typography
                        component="h1"
                        variant="h5"
                        className={classes.headerText}
                    >
                        Accept Terms and Conditions
                    </Typography>
                </div>
                <div className={classes.body}>
                    <Typography
                        component="div"
                        variant="h6"
                        className={classes.bodyText}
                    >
                        To proceed, please accept our latest Terms and
                        Conditions.
                    </Typography>
                    <div className={classes.buttonWrapper}>
                        <LoaderButton
                            disabled={updateUserProfileMutationData.isLoading}
                            color="primary"
                            type="button"
                            variant="contained"
                            onClick={onAccept}
                            loading={updateUserProfileMutationData.isLoading}
                        >
                            Accept
                        </LoaderButton>
                        <Button
                            color="secondary"
                            type="button"
                            variant="contained"
                            onClick={onViewTerms}
                        >
                            View Terms and Conditions
                        </Button>
                    </div>
                </div>
            </Paper>
        </Modal>
    );
}

const reduxState = (state: IAppState) => ({
    ...state.appConfigStoreState,
    ...state.userStoreState
});

export default withStyles(styles)(connect(reduxState)(TermsAcceptanceDialog));
